import jsPDF from "jspdf";

interface Profile {
  full_name: string;
  email?: string;
  phone_number?: string;
  location?: string;
  linkedin_url?: string;
  github_url?: string;
  website_url?: string;
  summary?: string;
}

interface Experience {
  company_name: string;
  position: string;
  start_date: string;
  end_date?: string;
  description?: string;
  achievements?: string[];
}

interface Education {
  degree_or_certification: string;
  institution: string;
  status?: string;
}

interface Skill {
  skill_name: string;
  category: string;
}

interface Competency {
  competency: string;
}

interface ResumeData {
  profile: Profile;
  experiences: Experience[];
  education: Education[];
  skills: Skill[];
  competencies: Competency[];
}

// Helper to clean text of HTML entities and special characters
const cleanText = (text: string): string => {
  return text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#x26;/g, "&")
    .replace(/&#x3C;/g, "<")
    .replace(/&#x3E;/g, ">")
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/→/g, "-")
    .replace(/•/g, "-")
    .trim();
};

// Helper to format URL for display
const formatUrl = (url: string): string => {
  return url
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "");
};

export const generateATSResume = (data: ResumeData): void => {
  const { profile, experiences, education, skills, competencies } = data;
  
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter", // Standard US letter for ATS
  });

  // Set PDF metadata for ATS systems
  doc.setProperties({
    title: `${profile.full_name} - Resume`,
    subject: "Professional Resume",
    author: profile.full_name,
    keywords: skills.map(s => s.skill_name).join(", "),
    creator: "Resume Builder",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 50;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  // Colors
  const primaryColor: [number, number, number] = [30, 64, 175]; // Professional blue
  const textColor: [number, number, number] = [31, 41, 55]; // Dark gray
  const mutedColor: [number, number, number] = [107, 114, 128]; // Muted gray

  const checkPageBreak = (height: number): void => {
    if (yPos + height > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
    }
  };

  const addSectionHeader = (title: string): void => {
    checkPageBreak(30);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text(title.toUpperCase(), margin, yPos);
    yPos += 4;
    
    // Underline
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(1.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 14;
    
    doc.setTextColor(...textColor);
  };

  // ==================== HEADER ====================
  // Name
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...textColor);
  doc.text(cleanText(profile.full_name), pageWidth / 2, yPos, { align: "center" });
  yPos += 20;

  // Contact line
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...mutedColor);
  
  const contactParts: string[] = [];
  if (profile.email) contactParts.push(cleanText(profile.email));
  if (profile.phone_number) contactParts.push(cleanText(profile.phone_number));
  if (profile.location) contactParts.push(cleanText(profile.location));
  
  if (contactParts.length > 0) {
    doc.text(contactParts.join("  |  "), pageWidth / 2, yPos, { align: "center" });
    yPos += 14;
  }

  // Links line
  const linkParts: string[] = [];
  if (profile.linkedin_url) linkParts.push(formatUrl(profile.linkedin_url));
  if (profile.website_url) linkParts.push(formatUrl(profile.website_url));
  if (profile.github_url) linkParts.push(formatUrl(profile.github_url));
  
  if (linkParts.length > 0) {
    doc.setTextColor(...primaryColor);
    doc.text(linkParts.join("  |  "), pageWidth / 2, yPos, { align: "center" });
    yPos += 20;
  } else {
    yPos += 6;
  }

  doc.setTextColor(...textColor);

  // ==================== PROFESSIONAL SUMMARY ====================
  if (profile.summary) {
    addSectionHeader("Professional Summary");
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const summaryText = cleanText(profile.summary);
    const summaryLines = doc.splitTextToSize(summaryText, contentWidth);
    
    summaryLines.forEach((line: string) => {
      checkPageBreak(14);
      doc.text(line, margin, yPos);
      yPos += 14;
    });
    yPos += 8;
  }

  // ==================== CORE COMPETENCIES ====================
  if (competencies.length > 0) {
    addSectionHeader("Core Competencies");
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    
    // Create a clean bullet-separated list
    const competencyList = competencies.map(c => cleanText(c.competency)).join("  •  ");
    const competencyLines = doc.splitTextToSize(competencyList, contentWidth);
    
    competencyLines.forEach((line: string) => {
      checkPageBreak(14);
      doc.text(line, margin, yPos);
      yPos += 14;
    });
    yPos += 8;
  }

  // ==================== PROFESSIONAL EXPERIENCE ====================
  if (experiences.length > 0) {
    addSectionHeader("Professional Experience");

    experiences.forEach((exp) => {
      checkPageBreak(50);
      
      // Company name and dates on same line
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(cleanText(exp.company_name), margin, yPos);
      
      const dateText = exp.end_date 
        ? `${cleanText(exp.start_date)} - ${cleanText(exp.end_date)}`
        : `${cleanText(exp.start_date)} - Present`;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(dateText, pageWidth - margin, yPos, { align: "right" });
      yPos += 14;

      // Position
      doc.setFont("helvetica", "bolditalic");
      doc.setFontSize(10);
      doc.text(cleanText(exp.position), margin, yPos);
      yPos += 14;

      // Description
      if (exp.description) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const descText = cleanText(exp.description);
        const descLines = doc.splitTextToSize(descText, contentWidth);
        descLines.forEach((line: string) => {
          checkPageBreak(14);
          doc.text(line, margin, yPos);
          yPos += 13;
        });
        yPos += 2;
      }

      // Achievements as bullet points
      if (exp.achievements && exp.achievements.length > 0) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        
        exp.achievements.forEach((achievement) => {
          checkPageBreak(16);
          const bulletText = cleanText(achievement);
          const achieveLines = doc.splitTextToSize(bulletText, contentWidth - 15);
          
          // First line with bullet
          doc.text("•", margin + 5, yPos);
          doc.text(achieveLines[0], margin + 15, yPos);
          yPos += 13;
          
          // Remaining lines indented
          for (let i = 1; i < achieveLines.length; i++) {
            checkPageBreak(13);
            doc.text(achieveLines[i], margin + 15, yPos);
            yPos += 13;
          }
        });
      }
      yPos += 10;
    });
  }

  // ==================== EDUCATION & CERTIFICATIONS ====================
  if (education.length > 0) {
    addSectionHeader("Education & Certifications");
    
    // Group education by institution for cleaner display
    const grouped = education.reduce((acc, edu) => {
      const inst = cleanText(edu.institution);
      if (!acc[inst]) acc[inst] = [];
      acc[inst].push(edu);
      return acc;
    }, {} as Record<string, Education[]>);

    Object.entries(grouped).forEach(([institution, items]) => {
      checkPageBreak(30);
      
      // Institution name
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(institution, margin, yPos);
      yPos += 14;
      
      // List certifications under this institution
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      
      items.forEach((edu) => {
        checkPageBreak(14);
        let text = cleanText(edu.degree_or_certification);
        if (edu.status) text += ` (${cleanText(edu.status)})`;
        
        const lines = doc.splitTextToSize(`• ${text}`, contentWidth - 10);
        lines.forEach((line: string, idx: number) => {
          checkPageBreak(13);
          doc.text(line, margin + (idx === 0 ? 5 : 15), yPos);
          yPos += 13;
        });
      });
      yPos += 6;
    });
  }

  // ==================== TECHNICAL SKILLS ====================
  if (skills.length > 0) {
    addSectionHeader("Technical Skills");

    // Group skills by category
    const skillsByCategory = skills.reduce((acc, skill) => {
      const cat = cleanText(skill.category);
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(cleanText(skill.skill_name));
      return acc;
    }, {} as Record<string, string[]>);

    doc.setFontSize(10);
    
    Object.entries(skillsByCategory).forEach(([category, categorySkills]) => {
      checkPageBreak(16);
      
      doc.setFont("helvetica", "bold");
      const categoryLabel = `${category}: `;
      doc.text(categoryLabel, margin, yPos);
      
      const categoryWidth = doc.getTextWidth(categoryLabel);
      
      doc.setFont("helvetica", "normal");
      const skillsText = categorySkills.join(", ");
      const availableWidth = contentWidth - categoryWidth;
      const skillLines = doc.splitTextToSize(skillsText, availableWidth);
      
      // First line after category label
      doc.text(skillLines[0], margin + categoryWidth, yPos);
      yPos += 14;
      
      // Additional lines
      for (let i = 1; i < skillLines.length; i++) {
        checkPageBreak(14);
        doc.text(skillLines[i], margin, yPos);
        yPos += 14;
      }
    });
  }

  // ==================== SAVE ====================
  const fileName = `${profile.full_name.replace(/\s+/g, "_")}_Resume.pdf`;
  doc.save(fileName);
};
